import { useState, useRef, useEffect } from 'react';
import Button from '../atoms/Button';
import Portal from '../atoms/Portal';
import { ChevronDown } from 'lucide-react';

export default function SplitButton({
  label,
  icon: MainIcon,
  onMainClick,
  variant = 'primary',
  size = 'md',
  options = [],
  onOptionSelect,
  disabled = false,
  mainDisabled = false,
  title,
  fullWidth = false,
  className = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, minWidth: 180 });
  const containerRef = useRef(null);
  const popoverRef = useRef(null);

  const toggleDropdown = () => {
    if (disabled) return;
    if (!isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        minWidth: Math.max(rect.width, 180),
      });
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      const isOutsideTrigger = containerRef.current && !containerRef.current.contains(e.target);
      const isOutsidePopover = popoverRef.current && !popoverRef.current.contains(e.target);

      if (isOutsideTrigger && isOutsidePopover) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const rootClasses = [
    'split-button',
    fullWidth ? 'split-button--full' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={rootClasses} ref={containerRef}>
      <Button
        variant={variant}
        size={size}
        icon={MainIcon}
        onClick={onMainClick}
        disabled={disabled || mainDisabled}
        title={title}
      >
        {label}
      </Button>
      <Button
        variant={variant}
        size={size}
        className="split-button__trigger"
        onClick={toggleDropdown}
        disabled={disabled}
        aria-label="More actions"
      >
        <ChevronDown size={16} />
      </Button>

      {isOpen && (
        <Portal>
          <div
            ref={popoverRef}
            className="portal-popover custom-dropdown-menu split-button-popover"
            style={{
              top: coords.top,
              left: coords.left,
              minWidth: coords.minWidth,
            }}
          >
            {options.map((opt, idx) => {
              const itemLabel = typeof opt === 'object' ? opt.label : opt;
              const OptIcon = typeof opt === 'object' ? opt.icon : null;
              const danger = typeof opt === 'object' && opt.variant === 'danger';
              const optDisabled = typeof opt === 'object' && opt.disabled;
              const optTitle = typeof opt === 'object' ? opt.title : undefined;
              return (
                <div
                  key={idx}
                  className={`dropdown-item${danger ? ' dropdown-item--danger' : ''}${optDisabled ? ' dropdown-item--disabled' : ''}`}
                  onClick={() => {
                    if (optDisabled) return;
                    if (onOptionSelect) onOptionSelect(opt);
                    setIsOpen(false);
                  }}
                  title={optTitle}
                >
                  {OptIcon && <OptIcon size={14} />}
                  <span>{itemLabel}</span>
                </div>
              );
            })}
          </div>
        </Portal>
      )}
    </div>
  );
}
